import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Workflow, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { OrgSales360 } from "@/components/common/OrgSales360";
import { useAuth } from "@/context/AuthContext";
import { SALES_STAGES, FUNNEL_STAGES, STAGE_META, PRIO_META, stageLabel, prioLabel } from "@/lib/sales";
import { translateApiError } from "@/lib/apiErrors";

export default function SalesPipeline() {
  const { can } = useAuth();
  const { t } = useTranslation();
  const canWrite = can("sales", "write");
  const [funnel, setFunnel] = useState(null);
  const [stage, setStage] = useState("");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  const loadFunnel = useCallback(() => {
    api.get("/sales/dashboard").then((r) => setFunnel(r.data.pipeline)).catch(() => {});
  }, []);
  const loadList = useCallback(() => {
    setData(null);
    const params = { page_size: 200, ...(stage ? { sales_stage: stage } : {}) };
    api.get("/sales/candidates", { params }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
  }, [stage]);

  useEffect(() => { loadFunnel(); }, [loadFunnel]);
  useEffect(() => { loadList(); }, [loadList]);

  const changeStage = async (orgId, newStage, e) => {
    e.stopPropagation();
    try {
      await api.patch(`/sales/organizations/${orgId}/stage`, { sales_stage: newStage });
      toast.success(t("sales.pipeline.toastStage", { stage: stageLabel(newStage) }));
      loadFunnel(); loadList();
    } catch (err) { toast.error(translateApiError(t, err.response?.data?.detail)); }
  };

  return (
    <div data-testid="sales-pipeline-page">
      <PageHeader title={t("sales.pipeline.title")} subtitle={t("sales.pipeline.subtitle")} />

      <div data-testid="pipeline-funnel" className="mb-5 flex flex-wrap gap-2">
        <button data-testid="pipeline-chip-all" onClick={() => setStage("")}
          className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${stage === "" ? "border-fundio-gold bg-fundio-gold/12 text-fundio-gold-deep" : "border-fundio-border bg-white text-fundio-muted hover:border-fundio-gold"}`}>
          {t("sales.pipeline.all")}
        </button>
        {FUNNEL_STAGES.map((s) => (
          <button key={s} data-testid={`pipeline-chip-${s}`} onClick={() => setStage(s)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${stage === s ? "border-fundio-gold bg-fundio-gold/12 text-fundio-gold-deep" : "border-fundio-border bg-white text-fundio-muted hover:border-fundio-gold"}`}>
            {stageLabel(s)}<span className="ml-1.5 font-mono text-[10px] text-fundio-faint" data-testid={`pipeline-count-${s}`}>{funnel?.[s] ?? "·"}</span>
          </button>
        ))}
      </div>

      <Panel testid="sales-pipeline-panel" noPad title={stage ? t("sales.pipeline.stagePrefix", { stage: stageLabel(stage) }) : t("sales.pipeline.allOrgs")}>
        {data === null ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
        ) : data.items.length === 0 ? (
          <div className="p-4"><EmptyState icon={Workflow} title={t("sales.pipeline.emptyTitle")} description={t("sales.pipeline.emptyDesc")} testid="pipeline-empty" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
                  <th className="px-4 py-3">{t("sales.candidates.hOrg")}</th><th className="px-4 py-3">{t("sales.candidates.hLocations")}</th><th className="px-4 py-3">{t("sales.candidates.hSales")}</th>
                  <th className="px-4 py-3">{t("sales.candidates.hOffer")}</th><th className="px-4 py-3">{t("sales.pipeline.hStage")}</th><th className="px-4 py-3 text-right">{t("common.action")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fundio-border">
                {data.items.map((r) => (
                  <tr key={r.organization_id} data-testid={`pipeline-row-${r.organization_id}`} onClick={() => setSelected(r.organization_id)} className="cursor-pointer bg-white transition-colors hover:bg-fundio-subtle">
                    <td className="px-4 py-3 font-medium text-fundio-ink">{r.org_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-fundio-ink">{r.location_count}</td>
                    <td className="px-4 py-3"><StatusBadge map={PRIO_META} value={r.sales_priority} label={prioLabel(r.sales_priority)} /></td>
                    <td className="px-4 py-3 text-xs font-semibold text-fundio-ink">{r.recommended_offer}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <select data-testid={`pipeline-stage-${r.organization_id}`} disabled={!canWrite} value={r.sales_stage}
                        onChange={(e) => changeStage(r.organization_id, e.target.value, e)}
                        className="rounded-lg border border-fundio-border bg-white px-2 py-1 text-xs text-fundio-ink outline-none focus:border-fundio-gold disabled:opacity-60">
                        {SALES_STAGES.map((s) => <option key={s} value={s}>{stageLabel(s)}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1 text-sm font-semibold text-fundio-ink">{t("common.open")} <ChevronRight className="h-4 w-4" /></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && <div className="border-t border-fundio-border px-4 py-2 text-xs text-fundio-muted">{t("common.orgsCount", { count: data.total })}</div>}
      </Panel>

      <OrgSales360 organizationId={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={() => { loadFunnel(); loadList(); }} />
    </div>
  );
}
